"use client";

import {
  Badge,
  Box,
  HStack,
  Stack,
  Text,
  type BadgeProps,
  type BoxProps,
  type StackProps,
} from "@chakra-ui/react";
import type { ReactNode } from "react";

export type GenericDrawerInfoItem = {
  label: ReactNode;
  colorPalette?: BadgeProps["colorPalette"];
  bg?: BadgeProps["bg"];
  borderColor?: BadgeProps["borderColor"];
  color?: BadgeProps["color"];
};

export type GenericDrawerInfoProps = Omit<BoxProps, "children"> & {
  description?: ReactNode;
  items?: GenericDrawerInfoItem[];
  itemsJustify?: StackProps["justify"];
  children?: ReactNode;
};

export default function GenericDrawerInfo({
  description,
  items = [],
  itemsJustify = "flex-start",
  children,
  p = "3",
  borderWidth = "1",
  borderColor = "moveon.surface.border",
  borderRadius = "md",
  bg = "moveon.surface.elevatedBg",
  ...rest
}: GenericDrawerInfoProps) {
  const hasItems = items.length > 0;

  return (
    <Box
      p={p}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      bg={bg}
      {...rest}
    >
      <Stack gap="3" align="stretch">
        {description ? (
          <Text fontSize="sm" color="moveon.text.muted" lineHeight="tall">
            {description}
          </Text>
        ) : null}

        {hasItems ? (
          <HStack gap="2" align="start" justify={itemsJustify} flexWrap="wrap">
            {items.map((item, index) => (
              <Badge
                key={index}
                variant="surface"
                colorPalette={item.colorPalette ?? "gray"}
                bg={item.bg}
                borderColor={item.borderColor}
                color={item.color}
              >
                {item.label}
              </Badge>
            ))}
          </HStack>
        ) : null}

        {children}
      </Stack>
    </Box>
  );
}
